import type { Json } from "../integrations/supabase/types";
import { typedSupabase } from "../lib/typed-supabase";
import type { ClientActivity, CreateClientActivityRequest } from "@flowstate/types";
import type { IService, ServiceResponse } from "./service-registry";

const safeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value as string[];
  }
  return [];
};

/**
 * Client activity service — handles creation and lifecycle of client activity records.
 */
export class ClientActivityService implements IService {
  readonly name = "ClientActivityService";

  async initialize(): Promise<void> {
    // no-op
  }

  async dispose(): Promise<void> {
    // no-op
  }

  async createClientActivity(
    data: CreateClientActivityRequest
  ): Promise<ServiceResponse<ClientActivity>> {
    try {
      const { data: { user }, error: authError } = await typedSupabase.auth.getUser();
      if (authError || !user) {
        return {
          success: false,
          error: "Authentication required",
          code: "AUTH_REQUIRED",
          service: this.name,
          context: { authError },
        };
      }

      const insertPayload = {
        ...data,
        metadata: data.metadata ?? {},
        attachments: data.attachments ?? [],
        created_by: user.id,
      };

      const { data: insertData, error } = await typedSupabase
        .from("client_activities")
        .insert(insertPayload)
        .select("*")
        .single();

      if (error || !insertData) {
        return {
          success: false,
          error: error?.message || "Failed to create client activity",
          code: "CREATE_ACTIVITY_FAILED",
          service: this.name,
          context: { error, data },
        };
      }

      const activity: ClientActivity = {
        id: insertData.id,
        client_id: insertData.client_id,
        type: insertData.type as ClientActivity["type"],
        title: insertData.title,
        description: insertData.description ?? undefined,
        outcome: insertData.outcome as ClientActivity["outcome"] | undefined,
        duration_minutes: insertData.duration_minutes ?? undefined,
        scheduled_at: insertData.scheduled_at ?? undefined,
        completed_at: insertData.completed_at ?? undefined,
        created_by: insertData.created_by,
        assigned_to: insertData.assigned_to ?? undefined,
        related_task_id: insertData.related_task_id ?? undefined,
        related_deal_id: insertData.related_deal_id ?? undefined,
        metadata: (insertData.metadata as Json) ?? undefined,
        attachments: safeStringArray(insertData.attachments),
        created_at: insertData.created_at,
        updated_at: insertData.updated_at,
      };

      return { success: true, data: activity };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        code: "CREATE_ACTIVITY_FAILED",
        service: this.name,
        context: { data, error },
      };
    }
  }
}

export const clientActivityService = new ClientActivityService();

/** Backwards-compatible alias used by legacy consumers in packages/core. */
export const clientService = clientActivityService;
